import { FileX, Loader2, ScanLine } from "lucide-react"
import type { PDFPageProxy, RenderTask } from "pdfjs-dist"
import * as React from "react"

import { CursorLayer } from "./cursor-layer"
import { DrawLayer } from "./draw-layer"
import { FindingOverlay } from "./finding-overlay"

import { useCtrlWheelZoom, useDragPan, useWheelPan } from "@/lib/pan-zoom"
import { cn } from "@/lib/utils"

import type { RemoteCursor } from "../api"
import type { Finding, FindingId, PointRect } from "../data/review-data"
import { clampZoom, ZOOM_MAX, ZOOM_MIN } from "./pdf-controls"
import { getPdfDocument } from "./pdf-loader"
import {
  SIDE_GUTTER,
  canPanSurface,
  cappedRasterZoom,
  centreSurfaceOn,
  clampSurfaceView,
  initialView,
  panSurface,
  zoomSurfaceAround,
  type StageRect,
  type SurfaceBounds,
  type SurfaceView,
} from "./surface-view"

/**
 * The document surface for a review.
 *
 * Renders one page of the PDF via pdf.js and overlays finding boxes on top.
 *
 * Coordinate contract (matches backend `[x0,y0,x1,y1]` top-left origin):
 *   canvas backing-store size == pdf.js viewport size == CSS display size.
 * Overlay boxes convert PDF points to CSS pixels with the same scale ratio
 * pdf.js used to render, so they land pixel-exact on the text.
 *
 * DPR is not applied to the canvas. Multiplying by devicePixelRatio makes a
 * size/aspect mismatch between backing store and display, drifting every
 * overlay proportional to y. Sharper text is not worth misaligned findings.
 *
 * Scale posture (long docs with many pages):
 *   – Only the current page is decoded; PDFPageProxy.cleanup() releases the
 *     previous page's operator list before decoding a new one.
 *   – Every render is a real pdf.js RenderTask that we cancel() when the
 *     inputs change, so a fast page-flip never leaves an orphan rendering
 *     into the canvas.
 *   – The document cache is LRU-bounded (see pdf-loader.ts).
 */

export interface PageGeometry {
  /** Rendered canvas size in CSS pixels — matches pdf.js viewport. */
  cssWidth: number
  cssHeight: number
  /** The page's native size in PDF points — the divisor for bbox math. */
  pointWidth: number
  pointHeight: number
  zoom: number
  page: number
}

export function rectToStyle(rect: PointRect, geom: PageGeometry): React.CSSProperties {
  const scaleX = geom.cssWidth / geom.pointWidth
  const scaleY = geom.cssHeight / geom.pointHeight
  return {
    left: rect.x0 * scaleX,
    top: rect.y0 * scaleY,
    width: Math.max(3, (rect.x1 - rect.x0) * scaleX),
    height: Math.max(3, (rect.y1 - rect.y0) * scaleY),
  }
}

/**
 * Fixed hex colours — never theme tokens. This overlay sits on the
 * always-white PDF canvas, where a token that flips in dark mode would
 * vanish or swap severity meaning silently.
 */
export interface PageSection {
  key: string
  label: string
  y0: number
  y1: number
}

export interface DrawResult {
  bbox: PointRect
  section: PageSection | null
}

/**
 * A finding-fill target: the framed bbox should occupy about this share of the
 * pane's width, so a tiny label element gets enlarged, a large one only
 * modestly, and both keep enough surrounding text to be judged in context.
 */
const FOCUS_TARGET_FRACTION = 0.75
/**
 * Every focus click must produce a visible zoom bump — otherwise a wide
 * finding whose bbox already covers most of the page reads as "the button
 * did nothing but scroll". Guarantees at least this much magnification on
 * top of the current 1× baseline.
 */
const FOCUS_ZOOM_MIN = 1.5
/**
 * Never zoom past this on a focus request.
 *
 * Generous rather than cautious: a one-line finding on a dense label is the
 * common case, and stopping short of readable text makes the control feel like
 * it only scrolled. Past this the surrounding text leaves the viewport and the
 * reviewer loses the context the finding is judged in.
 */
const FOCUS_ZOOM_MAX = 3.5

export interface FocusRequest {
  findingId: FindingId
  /** Bumped by the parent for each focus click, so re-focus on the same finding still fires. */
  nonce: number
}

export interface PdfSurfaceProps {
  documentUrl: string
  label: string
  subtitle?: string
  /**
   * The pipeline has not finished, so nothing is marked on the page yet. Said
   * in the header rather than over the document: the label is the reviewer's
   * own upload and is theirs to read while they wait — what is missing is the
   * review's marks, not access to the file.
   */
  awaitingFindings?: boolean
  page: number
  /** The document's real length, read from the file once pdf.js opens it. The API does
   *  not publish a page count, and the two documents in a compare are different
   *  lengths, so each surface reports its own. */
  onPageCount?: (count: number) => void
  zoom: number
  findings: Finding[]
  activeFindingId: FindingId | null
  onSelectFinding: (id: FindingId) => void
  /**
   * The width a page occupies at zoom 1, in CSS pixels.
   *
   * Normally the pane's own width, so a document fills the space it is given.
   * The compare view sets both panes to the same number instead: two documents
   * at the same zoom must render at the same size, or the reviewer is comparing
   * one against a smaller copy of the other and every visual difference is
   * suspect.
   */
  baseWidth?: number
  /**
   * Whether finding boxes are drawn over the page.
   *
   * A label carrying thirty marks is hard to read as a label, and part of
   * reviewing one is seeing the artwork as the client printed it. Hiding the
   * boxes is a view of the same page, not a change to the review, so nothing
   * about the findings themselves depends on it.
   */
  showBoxes?: boolean
  /**
   * A focus request from the parent. When set, PdfSurface computes the zoom
   * that fits the finding's bbox to a comfortable fraction of the viewport,
   * asks the parent to set that zoom, then — once the render has landed at
   * that zoom — scrolls the bbox to the middle of the viewport.
   */
  focusRequest?: FocusRequest | null
  /** Called when PdfSurface needs the parent to set a specific zoom. */
  onZoomChange?: (zoom: number) => void
  /**
   * Called when the reader zooms the page themselves, with Ctrl/⌘ + wheel or a
   * pinch. Defaults to `onZoomChange`; a parent that frames findings passes its
   * own, because a zoom the reader chose ends the frame where one it asked for
   * does not.
   */
  onManualZoom?: (zoom: number) => void
  cursors?: RemoteCursor[]
  className?: string
  showTitle?: boolean
  /** Identity beyond the filename — registration number, version — shown in the
   *  header band beside the document's name. */
  provenance?: string
  /**
   * Read this page through the OCR lane, for a page the gate misjudged.
   *
   * Omitted where it cannot apply — a reader who may not spend, a review
   * already running, or one parked on its own unread-pages question — rather
   * than rendered disabled, because a dead control invites a click that can
   * never work.
   */
  onReReadPage?: (page: number) => void
  reReadPending?: boolean
  drawMode?: boolean
  sections?: PageSection[]
  onDrawComplete?: (result: DrawResult) => void
  onDrawCancel?: () => void
}

/** A finding's box in the page's own pixels (at zoom 1), for framing it. */
function bboxToStageRect(bbox: PointRect, geom: PageGeometry): StageRect {
  const scaleX = geom.cssWidth / geom.pointWidth
  const scaleY = geom.cssHeight / geom.pointHeight
  return {
    x: bbox.x0 * scaleX,
    y: bbox.y0 * scaleY,
    w: (bbox.x1 - bbox.x0) * scaleX,
    h: (bbox.y1 - bbox.y0) * scaleY,
  }
}

/**
 * The sharp re-render waits for the zoom to hold still this long. The page is
 * scaled on screen at once; re-rasterising on every wheel notch would only
 * queue renders nobody sees.
 */
const RASTER_SETTLE_MS = 180
/** The wheel counts as still moving the page for this long after its last event. */
const WHEEL_PAN_IDLE_MS = 160
/** The page's height before its first render lands, so the frame has something to lay out. */
const PLACEHOLDER_HEIGHT = 220

export function PdfSurface({
  documentUrl,
  label,
  subtitle,
  awaitingFindings = false,
  page,
  zoom,
  findings,
  activeFindingId,
  onSelectFinding,
  baseWidth,
  showBoxes = true,
  focusRequest,
  onZoomChange,
  onManualZoom,
  cursors = [],
  className,
  onPageCount,
  showTitle = true,
  provenance,
  onReReadPage,
  reReadPending,
  drawMode = false,
  sections = [],
  onDrawComplete,
  onDrawCancel,
}: PdfSurfaceProps): JSX.Element {
  const frameRef = React.useRef<HTMLDivElement>(null)
  const canvasRef = React.useRef<HTMLCanvasElement>(null)
  const [geometry, setGeometry] = React.useState<PageGeometry | null>(null)
  const [error, setError] = React.useState<string | null>(null)
  /** Set when a finding named a page beyond the end of this document. */
  const [outOfRangePage, setOutOfRangePage] = React.useState<number | null>(null)
  // Held in a ref so an inline callback does not re-run the render effect, which
  // would redraw the page on every parent render.
  const onPageCountRef = React.useRef(onPageCount)
  onPageCountRef.current = onPageCount
  // Read by the selection effect without making it a trigger: the list is
  // rebuilt whenever a conversation count changes, and that must not pull the
  // view back to the selected finding.
  const findingsRef = React.useRef(findings)
  findingsRef.current = findings

  /**
   * The frame the page sits in: the pane below the title band. It never
   * scrolls. The page is laid out once at its zoom-1 size and scaled and moved
   * inside it, as the report's document view is, so a zoom can neither push
   * the page out of the pane nor wait on pdf.js to show.
   */
  const [frame, setFrame] = React.useState({ width: 0, height: 0 })
  React.useLayoutEffect(() => {
    const el = frameRef.current
    if (!el || typeof ResizeObserver === "undefined") return
    const measure = () => {
      const rect = el.getBoundingClientRect()
      // Rounded: a ResizeObserver reports sub-pixel changes, and the width
      // feeds the render effect. Unrounded, a fractional reflow re-renders the
      // whole page for a change nobody can see, which reads as flicker.
      const next = { width: Math.round(rect.width), height: Math.round(rect.height) }
      setFrame((current) =>
        current.width === next.width && current.height === next.height ? current : next,
      )
    }
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    return () => {
      ro.disconnect()
    }
  }, [])

  const containerWidth = Math.max(frame.width - SIDE_GUTTER * 2, 0)
  /** The page's width at zoom 1: the caller's, or the pane's own. */
  const pageBaseWidth =
    baseWidth ?? (containerWidth > 0 ? Math.max(320, Math.min(containerWidth, 1120)) : 0)

  /**
   * The rendered page, sized to the current layout. A resize changes the
   * page's width before pdf.js has drawn it again; the bitmap is stretched to
   * the new size meanwhile, and the boxes follow the layout, not the bitmap.
   */
  const layoutGeom = React.useMemo<PageGeometry | null>(
    () =>
      geometry && pageBaseWidth > 0
        ? {
            ...geometry,
            cssWidth: pageBaseWidth,
            cssHeight: (geometry.pointHeight / geometry.pointWidth) * pageBaseWidth,
          }
        : null,
    [geometry, pageBaseWidth],
  )
  const bounds = React.useMemo<SurfaceBounds>(
    () => ({
      frameW: frame.width,
      frameH: frame.height,
      pageW: pageBaseWidth,
      pageH: layoutGeom?.cssHeight ?? PLACEHOLDER_HEIGHT,
    }),
    [frame.width, frame.height, pageBaseWidth, layoutGeom?.cssHeight],
  )
  // Handlers run between renders (a wheel delivers events faster than React
  // commits), so they read the latest bounds and view from refs.
  const boundsRef = React.useRef(bounds)
  boundsRef.current = bounds

  const [rawView, setView] = React.useState<SurfaceView>(() => initialView(zoom))
  // Clamped on every render, so a resize or a page landing at a new height
  // never leaves the page stranded outside its gutters.
  const view = clampSurfaceView(rawView, bounds)
  const viewRef = React.useRef(view)
  viewRef.current = view
  const commitView = React.useCallback((next: SurfaceView): void => {
    viewRef.current = next
    setView(next)
  }, [])

  // A new page opens at its first line, at whatever zoom the reader is on.
  React.useLayoutEffect(() => {
    setView((current) => initialView(current.zoom))
  }, [page])

  // A zoom set from outside (the zoom control, the parent resetting it) keeps
  // the middle of the frame still. Zooms this surface made itself — the wheel,
  // a focus — set the view first and record the zoom here, so the prop
  // arriving after them changes nothing.
  //
  // Pointer-anchored zoom was tried and felt worse, so zooms always centre on
  // the frame rather than the cursor or pinch centre.
  const lastZoomPropRef = React.useRef(zoom)
  React.useLayoutEffect(() => {
    if (zoom === lastZoomPropRef.current) return
    lastZoomPropRef.current = zoom
    const b = boundsRef.current
    setView((current) =>
      current.zoom === zoom
        ? current
        : zoomSurfaceAround(clampSurfaceView(current, b), zoom, b.frameW / 2, b.frameH / 2, b),
    )
  }, [zoom])

  // Drag to move around a page larger than the frame, gliding on when let go.
  // Off while drawing, where a drag on the page is a box being drawn. `from`
  // is the view at the press, `live` the view each glide frame steps on from.
  const canPan = !drawMode && canPanSurface(view.zoom, bounds)
  const fromRef = React.useRef(view)
  const liveRef = React.useRef(view)
  const pan = useDragPan({
    onPress: () => {
      if (!canPan) return false
      fromRef.current = viewRef.current
      liveRef.current = viewRef.current
      return true
    },
    onMove: (dx, dy) => {
      liveRef.current = panSurface(fromRef.current, dx, dy, boundsRef.current)
      commitView(liveRef.current)
    },
    onGlide: (vx, vy) => {
      const current = liveRef.current
      const next = panSurface(current, vx, vy, boundsRef.current)
      liveRef.current = next
      commitView(next)
      return { x: next.tx !== current.tx, y: next.ty !== current.ty }
    },
  })
  const { stopGlide } = pan

  // A plain wheel moves the page, since the frame itself no longer scrolls.
  // While it turns the page follows it directly rather than easing behind.
  const [wheelPanning, setWheelPanning] = React.useState(false)
  const wheelIdleRef = React.useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  React.useEffect(
    () => () => {
      clearTimeout(wheelIdleRef.current)
    },
    [],
  )
  useWheelPan(frameRef, (dx, dy) => {
    const current = viewRef.current
    const next = panSurface(current, dx, dy, boundsRef.current)
    if (next.tx === current.tx && next.ty === current.ty) return false
    stopGlide()
    commitView(next)
    setWheelPanning(true)
    clearTimeout(wheelIdleRef.current)
    wheelIdleRef.current = setTimeout(() => {
      setWheelPanning(false)
    }, WHEEL_PAN_IDLE_MS)
    return true
  })

  // Ctrl/⌘ + wheel (or a pinch) zooms on the pointer. `zoomRef` is the zoom as
  // last asked for, unrounded and ahead of the render: wheel notches arrive
  // faster than renders, each must multiply the one before it, and a pinch's
  // small steps would each round back to where they began if they started
  // from the stored hundredth.
  const zoomRef = React.useRef(zoom)
  React.useEffect(() => {
    // A zoom from elsewhere (the control, a focus) is where the next notch starts from.
    if (clampZoom(zoomRef.current) !== zoom) zoomRef.current = zoom
  }, [zoom])
  const wheeling = useCtrlWheelZoom(frameRef, (factor, x, y) => {
    const change = onManualZoom ?? onZoomChange
    if (!change || pageBaseWidth <= 0) return
    const before = clampZoom(zoomRef.current)
    zoomRef.current = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, zoomRef.current * factor))
    const next = clampZoom(zoomRef.current)
    if (next === before) return
    stopGlide()
    lastZoomPropRef.current = next
    commitView(zoomSurfaceAround(viewRef.current, next, x, y, boundsRef.current))
    change(next)
  })

  // Re-rasterise once the zoom holds still, at the zoom it settled on, so the
  // text is as sharp as it is large. Never below 1: a zoomed-out page is drawn
  // at its full width and scaled down.
  const [rasterZoom, setRasterZoom] = React.useState(() => Math.max(1, view.zoom))
  React.useEffect(() => {
    const target = Math.max(1, view.zoom)
    const id = setTimeout(() => {
      setRasterZoom(target)
    }, RASTER_SETTLE_MS)
    return () => {
      clearTimeout(id)
    }
  }, [view.zoom])

  // Selection (not a focus): a finding picked on this page is brought to the
  // middle of the frame, at the zoom the reader is on. Keyed on the selection
  // and the page that has landed, never on the zoom, so zooming does not pull
  // the reader back to the selected finding. Declared before the focus effect,
  // which owns the view for a selection that arrives with a focus request.
  const servedNonceRef = React.useRef<number | null>(null)
  const layoutGeomRef = React.useRef(layoutGeom)
  layoutGeomRef.current = layoutGeom
  const renderedPage = geometry?.page
  React.useEffect(() => {
    const geom = layoutGeomRef.current
    if (!activeFindingId || !geom) return
    if (focusRequest && servedNonceRef.current !== focusRequest.nonce) return
    const finding = findingsRef.current.find((f) => f.id === activeFindingId)
    if (finding?.placement !== "box" || finding.bbox?.page !== geom.page) return
    const b = boundsRef.current
    commitView(centreSurfaceOn(bboxToStageRect(finding.bbox, geom), viewRef.current.zoom, b))
  }, [activeFindingId, renderedPage, focusRequest, commitView])

  // A focus request from the parent: zoom so the finding's box fills a
  // comfortable share of the pane, and centre it. One step now that the page
  // is scaled rather than re-laid-out: the view is set at the new zoom at
  // once, and the parent is told the zoom so its control reads it.
  React.useEffect(() => {
    if (!focusRequest || !layoutGeom || bounds.frameW <= 0) return
    if (servedNonceRef.current === focusRequest.nonce) return
    const finding = findings.find((f) => f.id === focusRequest.findingId)
    if (!finding?.bbox) {
      servedNonceRef.current = focusRequest.nonce
      return
    }
    if (finding.bbox.page !== layoutGeom.page) return // wait for the page to turn

    const widthFraction = Math.max(
      (finding.bbox.x1 - finding.bbox.x0) / layoutGeom.pointWidth,
      0.02,
    )
    // FOCUS_ZOOM_MIN is a floor: even a box spanning most of the page still
    // magnifies on a focus click, so the button never feels inert. Rounded to
    // the precision the zoom control stores, so the prop that comes back
    // matches it exactly.
    const wanted = clampZoom(
      Math.max(FOCUS_ZOOM_MIN, Math.min(FOCUS_TARGET_FRACTION / widthFraction, FOCUS_ZOOM_MAX)),
    )
    servedNonceRef.current = focusRequest.nonce
    stopGlide()
    lastZoomPropRef.current = wanted
    zoomRef.current = wanted
    commitView(centreSurfaceOn(bboxToStageRect(finding.bbox, layoutGeom), wanted, bounds))
    if (wanted !== zoom) onZoomChange?.(wanted)
  }, [focusRequest, layoutGeom, bounds, findings, zoom, onZoomChange, stopGlide, commitView])

  React.useEffect(() => {
    if (!documentUrl || pageBaseWidth <= 0) return

    let cancelled = false
    let renderTask: RenderTask | null = null
    let renderedPdfPage: PDFPageProxy | null = null
    setError(null)

    void (async () => {
      try {
        const doc = await getPdfDocument(documentUrl)
        if (cancelled) return
        onPageCountRef.current?.(doc.numPages)

        // A finding can name a page this document does not have — a stale
        // record, or an upload replaced by a shorter file. Render the nearest
        // real page and say so, rather than surfacing pdf.js's own wording for
        // a request the reviewer never made.
        const requested = Math.min(Math.max(page, 1), doc.numPages)
        if (requested !== page) {
          setOutOfRangePage(page)
        } else {
          setOutOfRangePage(null)
        }

        const pdfPage = await doc.getPage(requested)
        if (cancelled) return
        renderedPdfPage = pdfPage

        const base = pdfPage.getViewport({ scale: 1 })
        // Laid out at the zoom-1 width and scaled on screen; drawn at the zoom
        // it is shown at, so the scaled bitmap is not soft, up to a pixel cap:
        // past it a canvas is too big for iOS to draw at all, so the bitmap
        // stops growing and the on-screen scale covers the rest.
        const drawZoom = cappedRasterZoom(rasterZoom, pageBaseWidth, base.width, base.height)
        const viewport = pdfPage.getViewport({
          scale: (pageBaseWidth * drawZoom) / base.width,
        })

        const canvas = canvasRef.current
        const ctx = canvas?.getContext("2d")
        if (!canvas || !ctx) return

        // Render into a detached canvas and swap the finished frame in, rather
        // than resizing the live one. Assigning to canvas.width clears it to
        // transparent, and a re-render takes long enough to see, so the visible
        // page would blank on every zoom step.
        const offscreen = document.createElement("canvas")
        offscreen.width = viewport.width
        offscreen.height = viewport.height
        const offscreenCtx = offscreen.getContext("2d")
        if (!offscreenCtx) return

        renderTask = pdfPage.render({
          canvas: offscreen,
          canvasContext: offscreenCtx,
          viewport,
        })
        await renderTask.promise
        if (cancelled) return

        canvas.width = viewport.width
        canvas.height = viewport.height
        ctx.drawImage(offscreen, 0, 0)

        setGeometry({
          cssWidth: pageBaseWidth,
          cssHeight: (base.height / base.width) * pageBaseWidth,
          pointWidth: base.width,
          pointHeight: base.height,
          zoom: rasterZoom,
          page,
        })
      } catch (cause) {
        if (cancelled) return
        // pdf.js throws its own RenderingCancelledException when render()
        // is aborted; that is not an error to surface to the reviewer.
        if (cause instanceof Error && cause.name === "RenderingCancelledException") return
        setError(cause instanceof Error ? cause.message : "Could not render the PDF.")
      }
    })()

    return () => {
      cancelled = true
      // Cancel the outstanding render so a fast page-flip doesn't waste
      // CPU rendering a page nobody will see, and so the next render can
      // own the canvas without contending for it.
      renderTask?.cancel()
      // Free the previous page's operator list, glyph paths and worker
      // buffers. Without this, jumping around a long doc grows the worker
      // heap for every page visited.
      renderedPdfPage?.cleanup()
    }
  }, [documentUrl, page, rasterZoom, pageBaseWidth])

  return (
    <div
      className={cn(
        "relative flex h-full min-h-0 w-full flex-col overflow-hidden bg-[color:var(--canvas)]",
        className,
      )}
    >
      {/* The document's identity, outside the scroll area so it holds its place
          while the page moves beneath it. Inside, it drifted with the content
          and collided with the floating controls; two panes also disagreed on
          where their headers sat, because each was positioned by its own page.
          A fixed band gives both panes one baseline. */}
      {showTitle && (
        /* Centred over the page and unruled, rather than a bar across the pane:
           this names the document, so it belongs with the document rather than
           with the window's furniture. It stays outside the scroll area — inside,
           it drifted with the content and two panes disagreed on where their
           headers sat, because each was positioned by its own page. */
        // Clears the floating navigator above (12px offset + 44px tall).
        <div className="flex shrink-0 items-center justify-center gap-2 px-4 pb-0 pt-[64px]">
          <p className="truncate font-id text-caption text-text-3">
            {label}
            {subtitle && (
              <>
                <span aria-hidden="true"> · </span>
                {subtitle}
              </>
            )}
            {/* What a regulator identifies the document by, not just its
                filename: a reviewer with two labels open has to be able to say
                which registration each one belongs to without leaving the pane. */}
            {provenance && (
              <>
                <span aria-hidden="true"> · </span>
                <span className="tabular-nums">{provenance}</span>
              </>
            )}
            {/* The page's number with the document's name, in the band that
                holds still: on the sheet it scaled with a zoom and was carried
                out of view by it, so a zoomed reader lost which page they were on. */}
            <span aria-hidden="true"> · </span>
            <span className="tabular-nums">Page {page}</span>
          </p>
          {/* Beside the document it acts on, and named with the page it will
              re-read, so a reviewer pressing it knows exactly what they are
              spending a re-run on. */}
          {onReReadPage !== undefined && (
            <button
              type="button"
              onClick={() => {
                onReReadPage(page)
              }}
              disabled={reReadPending === true}
              className={cn(
                // Bordered and iconed: bare text beside the filename trail reads as more metadata.
                "inline-flex shrink-0 items-center gap-1.5 rounded-md border border-border",
                "bg-surface px-2 py-1 font-id text-caption text-text-2",
                "transition-colors duration-120 ease-out",
                "hover:border-border-strong hover:bg-surface-sunken hover:text-text",
                "disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-surface",
                "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--brand)]",
              )}
              title="Read this page through the OCR lane and run the review again"
            >
              {reReadPending === true ? (
                <Loader2 className="size-3.5 animate-spin" aria-hidden="true" />
              ) : (
                <ScanLine className="size-3.5" aria-hidden="true" />
              )}
              {reReadPending === true ? "Re-reading…" : `Re-read p${String(page)}`}
            </button>
          )}
          {outOfRangePage != null && (
            <span className="flex shrink-0 items-center gap-1.5 text-caption text-print">
              This finding names page {outOfRangePage}, which this document does not have.
            </span>
          )}
          {awaitingFindings && (
            <span className="flex shrink-0 items-center gap-1.5 text-caption text-text-3">
              <span className="size-1.5 animate-pulse rounded-full bg-brand motion-reduce:animate-none" />
              Nothing marked yet
            </span>
          )}
        </div>
      )}

      {/* The frame: a fixed window onto the page, as the report's document view
          has. It never scrolls; the page is scaled and moved inside it, so a
          zoom keeps the page in the pane instead of pushing its top and left
          out of reach. A drag, the wheel or Ctrl/⌘ + wheel moves or zooms it. */}
      <div
        ref={frameRef}
        className={cn(
          "relative min-h-0 flex-1 touch-none overflow-hidden",
          canPan && "select-none",
          canPan && (pan.dragging ? "cursor-grabbing" : "cursor-grab"),
        )}
        {...pan.handlers}
      >
        <div
          className={cn(
            "absolute left-0 top-0 origin-top-left",
            // A drag, a glide or a turning wheel follows the pointer directly;
            // only a reframe (the zoom control, a focus, a selection) eases.
            // Not before the first render lands, or the page would slide in from
            // the corner while the frame is first measured.
            layoutGeom != null &&
              !pan.dragging &&
              !pan.gliding &&
              !wheeling &&
              !wheelPanning &&
              "transition-transform duration-300 ease-out motion-reduce:transition-none",
          )}
          style={{
            width: pageBaseWidth || "100%",
            transform: `translate(${String(view.tx)}px, ${String(view.ty)}px) scale(${String(view.zoom)})`,
          }}
        >
          <div
            // The page is always white, so what separates it from the surround
            // depends on the theme: a ring where the two are close in value, a
            // shadow where they are not. Both are tokens so neither is decided here.
            className="relative overflow-hidden rounded-[3px] bg-white shadow-page ring-1 ring-[color:var(--canvas-page-ring)]"
          >
            <canvas
              ref={canvasRef}
              // Width 100% of the page rather than the bitmap's own size: the
              // bitmap is drawn at the zoom it is shown at, and between a resize
              // and the frame that answers it the old one is stretched to fit.
              style={{ width: "100%", height: "auto" }}
              className={cn(
                "block transition-opacity duration-300",
                (error != null || !documentUrl) && "hidden",
                // Held back a little while the pipeline reads, so the page
                // reads as "not finished with" at a glance. Shallow on
                // purpose: the reviewer still has to be able to check they
                // uploaded the right label.
                awaitingFindings && "opacity-85",
              )}
              aria-hidden="true"
            />
            {/* A band of light crossing the page while the pipeline reads it.
                Deliberately not a blur or a lock: the label is the reviewer's
                own upload and stays legible, so they can confirm they sent the
                right file. What is missing is the review's marks, not access.
                The status itself is in the header, where a screen reader finds
                it — this is decoration and is hidden from the tree. */}
            {awaitingFindings && !error && (
              <div
                aria-hidden="true"
                className="pointer-events-none absolute inset-0 motion-reduce:hidden"
              >
                {/* A fixed band, not a fraction of the page: a third of a tall
                    page is a tinted block that reads as damage to the document
                    rather than as something passing over it. */}
                <div
                  className="absolute inset-x-0 h-[160px] bg-gradient-to-b from-transparent via-brand/10 to-transparent"
                  style={{ animation: "pageScan 2.6s ease-in-out infinite" }}
                />
              </div>
            )}
            {!documentUrl && (
              <div
                role="status"
                className="flex h-[220px] flex-col items-center justify-center gap-1.5 px-6 text-center"
              >
                <FileX className="size-5 text-text-3" aria-hidden="true" />
                <span className="text-body-sm font-medium text-text-2">
                  No document on this review
                </span>
                <span className="text-caption text-text-3">
                  The printed label never landed, so there is nothing to show here.
                </span>
              </div>
            )}
            {documentUrl && !error && geometry === null && (
              // A 1MB label takes seconds to fetch and rasterise; a white sheet with
              // nothing on it reads as broken for every one of them.
              <div
                role="status"
                aria-live="polite"
                className="flex h-[220px] flex-col items-center justify-center gap-2 px-6 text-center"
              >
                <Loader2
                  className="size-5 animate-spin text-text-3 motion-reduce:animate-none"
                  aria-hidden="true"
                />
                <span className="text-body-sm text-text-2">Loading page {page}…</span>
              </div>
            )}
            {error && (
              <div
                role="alert"
                className="flex h-[220px] items-center justify-center px-6 text-center text-body-sm text-err"
              >
                {error}
              </div>
            )}
            {layoutGeom && (
              <>
                {showBoxes && (
                  <FindingOverlay
                    geom={layoutGeom}
                    findings={findings}
                    activeFindingId={activeFindingId}
                    onSelect={onSelectFinding}
                    zoom={view.zoom}
                  />
                )}
                <CursorLayer geom={layoutGeom} cursors={cursors} zoom={view.zoom} />
                {drawMode && (
                  <DrawLayer
                    geom={layoutGeom}
                    sections={sections}
                    onComplete={(r) => onDrawComplete?.(r)}
                    onCancel={() => onDrawCancel?.()}
                  />
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
